import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronRight } from 'lucide-react';
import { MouseGlow } from '@/components/site/MouseGlow';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { MagneticButton } from '@/components/site/MagneticButton';
import { HeroImage } from '@/components/site/HeroImage';
import { FAQList } from '@/components/site/FAQ';
import { CTABand } from '@/components/site/CTABand';
import { FaqSchema } from '@/components/site/Seo';
import { SITE } from '@/data/site';
import { LANDINGS, OWN_PRODUCTS } from '@/data/landings';

const LINK_CLS = 'text-white underline underline-offset-4 decoration-white/30 hover:decoration-white';

function Hero({ page }) {
  return (
    <section className="relative overflow-hidden">
      <MouseGlow intensity={0.3} />
      <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 md:pt-36 pb-20 md:pb-28">
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-ink-muted">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <ChevronRight size={12} />
          {page.crumbParent && (
            <>
              <Link to={page.crumbParent.to} className="hover:text-white transition-colors">{page.crumbParent.label}</Link>
              <ChevronRight size={12} />
            </>
          )}
          <span className="text-white">{page.crumbLabel}</span>
        </nav>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-x-10 md:gap-y-16">
          <div className="md:col-span-9">
            <SectionLabel as="h1" num="—" data-testid={`landing-${page.slug}-h1`} className="mt-2">
              {page.h1}
            </SectionLabel>
            <WordReveal
              as="p"
              text={page.headline}
              accent={page.accent}
              className="mt-8 text-4xl sm:text-5xl md:text-6xl lg:text-[78px] font-extrabold tracking-tightest leading-[1.02] text-balance"
            />
          </div>
          <div className="md:col-span-7 md:col-start-6 md:row-start-2">
            <Reveal delay={400}>
              <p className="text-lg md:text-xl text-ink-muted leading-relaxed max-w-xl">{page.intro}</p>
            </Reveal>
            <Reveal delay={520}>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <MagneticButton href={SITE.whatsapp} external variant="primary">
                  Get a fixed quote <ArrowUpRight size={16} />
                </MagneticButton>
                <MagneticButton to="/contact/" variant="ghost">Contact us</MagneticButton>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-5 md:col-start-1 md:row-start-2 self-end">
            <HeroImage src={page.image.src} alt={page.image.alt} testId={`landing-${page.slug}-hero-image`} />
          </div>
        </div>
      </div>
    </section>
  );
}

function Story({ story, num }) {
  return (
    <section className="relative py-24 md:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <SectionLabel num={num}>{story.label}</SectionLabel>
          <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">{story.heading}</h2>
        </div>
        <div className="md:col-span-8 space-y-6">
          {story.paras.map((para, i) => (
            <Reveal key={i} delay={i * 80}>
              <p className="text-lg text-ink-muted leading-relaxed">{para}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Cards({ cards, num }) {
  return (
    <section className="relative py-24 md:py-32 border-t border-white/10 bg-ink-surface/30">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionLabel num={num}>{cards.label}</SectionLabel>
        <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl text-balance">{cards.heading}</h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {cards.items.map((c, i) => (
            <Reveal key={c.k} delay={i * 60}>
              <div className="h-full rounded-[16px] border border-white/[0.12] bg-ink-bg p-6">
                <h3 className="text-xl font-semibold tracking-tight">{c.k}</h3>
                <p className="mt-3 text-sm text-ink-muted leading-relaxed">{c.v}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function KeyList({ list, num }) {
  return (
    <section className="relative py-24 md:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <SectionLabel num={num}>{list.label}</SectionLabel>
          <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">{list.heading}</h2>
        </div>
        <ul className="md:col-span-8 divide-y divide-white/10 border-y border-white/10">
          {list.items.map((w) => (
            <li key={w.k} className="py-6 grid grid-cols-1 md:grid-cols-12 gap-4">
              <h3 className="md:col-span-4 text-lg font-semibold tracking-tight">{w.k}</h3>
              <p className="md:col-span-8 text-ink-muted leading-relaxed">{w.v}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function DataTable({ table, num }) {
  return (
    <section className="relative py-24 md:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionLabel num={num}>{table.label}</SectionLabel>
        <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl text-balance">{table.heading}</h2>
        {table.intro && <p className="mt-6 text-lg text-ink-muted leading-relaxed max-w-3xl">{table.intro}</p>}
        <div className="mt-10 overflow-x-auto rounded-[16px] border border-white/[0.12]">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-ink-surface text-left text-[11px] uppercase tracking-[0.18em] text-ink-muted">
              <tr>
                {table.cols.map((c, i) => (
                  <th key={i} scope="col" className="px-5 py-4 font-normal">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {table.rows.map((row) => (
                <tr key={row[0]}>
                  <th scope="row" className="px-5 py-4 text-left font-medium text-white">{row[0]}</th>
                  {row.slice(1).map((cell, i) => (
                    <td key={i} className="px-5 py-4 text-ink-muted">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {table.note && <p className="mt-4 text-xs text-ink-muted">{table.note}</p>}
      </div>
    </section>
  );
}

function ChipColumns({ chips, num }) {
  return (
    <section className="relative py-24 md:py-32 border-t border-white/10 bg-ink-surface/30">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-14">
        {chips.map((col) => (
          <div key={col.heading}>
            <SectionLabel num={num}>Coverage</SectionLabel>
            <h2 className="mt-6 text-2xl md:text-3xl font-bold tracking-tight">{col.heading}</h2>
            <div className="mt-6 flex flex-wrap gap-2">
              {col.items.map((x) => (
                <span key={x} className="text-sm px-3.5 py-1.5 rounded-full border border-white/[0.12] text-white/85">{x}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Only products the team builds and runs itself: verifiable, no client claims. */
export function OwnProducts({ num }) {
  return (
    <section className="relative py-24 md:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionLabel num={num}>Live products</SectionLabel>
        <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight max-w-3xl text-balance">
          Apps we build and run ourselves
        </h2>
        <p className="mt-6 text-lg text-ink-muted leading-relaxed max-w-3xl">
          We do not only build for clients. Our team ships and supports its own products, so we deal with
          store reviews, OS updates and real users every week.
        </p>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
          {OWN_PRODUCTS.map((p) => (
            <Link
              key={p.name}
              to={p.caseStudy}
              className="group block rounded-[20px] border border-white/[0.12] bg-ink-surface p-7 hover:border-white/25 transition-colors"
            >
              <div className="text-[11px] uppercase tracking-[0.24em] text-ink-muted">{p.kind}</div>
              <h3 className="mt-2 text-2xl font-semibold tracking-tight flex items-center gap-2">
                {p.name} <ArrowUpRight size={18} className="text-ink-muted group-hover:text-white transition-colors" />
              </h3>
              <p className="mt-3 text-sm text-ink-muted leading-relaxed">{p.body}</p>
              <span className="mt-4 inline-block text-sm text-violet">Read the case study →</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function Cost({ cost, num }) {
  return (
    <section className="relative py-24 md:py-32 border-t border-white/10 bg-ink-surface/30">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionLabel num={num}>Cost &amp; timelines</SectionLabel>
        <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl text-balance">{cost.heading}</h2>
        <p className="mt-6 text-lg text-ink-muted leading-relaxed max-w-3xl">{cost.intro}</p>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {cost.tiers.map((t, i) => (
            <Reveal key={t.name} delay={i * 60}>
              <div className="h-full rounded-[16px] border border-white/[0.12] bg-ink-bg p-6">
                <h3 className="text-lg font-semibold tracking-tight">{t.name}</h3>
                <div className="mt-2 text-2xl font-bold text-violet tabular-nums">{t.from}</div>
                <p className="mt-3 text-sm text-ink-muted leading-relaxed">{t.note}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-sm text-ink-muted">
          Prices exclude GST.
          {cost.link && (
            <>
              {' '}
              <Link to={cost.link.to} className={LINK_CLS}>{cost.link.label}</Link>.
            </>
          )}
        </p>
      </div>
    </section>
  );
}

function Faq({ page, num }) {
  return (
    <section className="relative py-24 md:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <SectionLabel num={num}>FAQ</SectionLabel>
          <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">{page.faqHeading}</h2>
          <div className="mt-8 text-sm text-ink-muted">Related pages</div>
          <ul className="mt-3 space-y-2 text-sm">
            {page.links.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className={LINK_CLS}>{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-8">
          <FAQList items={page.faqs} testIdPrefix={`landing-${page.slug}-faq`} />
        </div>
      </div>
    </section>
  );
}

export default function Landing({ slug }) {
  const page = LANDINGS.find((l) => l.slug === slug);
  // Section order: the cost page leads with prices, the rest with context.
  const blocks = [
    page.cost?.first && ['cost', page.cost],
    page.story && ['story', page.story],
    page.cards && !page.cost?.first && ['cards', page.cards],
    page.table && ['table', page.table],
    page.list && ['list', page.list],
    page.cards && page.cost?.first && ['cards', page.cards],
    page.chips && ['chips', page.chips],
    page.proof && ['proof', true],
    page.cost && !page.cost.first && ['cost', page.cost],
  ].filter(Boolean);
  const num = (i) => String(i + 1).padStart(2, '0');

  return (
    <div data-testid={`landing-${slug}-page`}>
      <FaqSchema items={page.faqs} />
      <Hero page={page} />
      {blocks.map(([kind, data], i) => {
        const n = num(i);
        if (kind === 'story') return <Story key={kind} story={data} num={n} />;
        if (kind === 'cards') return <Cards key={kind} cards={data} num={n} />;
        if (kind === 'table') return <DataTable key={kind} table={data} num={n} />;
        if (kind === 'list') return <KeyList key={kind} list={data} num={n} />;
        if (kind === 'chips') return <ChipColumns key={kind} chips={data} num={n} />;
        if (kind === 'proof') return <OwnProducts key={kind} num={n} />;
        return <Cost key={kind} cost={data} num={n} />;
      })}
      <Faq page={page} num={num(blocks.length)} />
      <CTABand eyebrow={page.cta.eyebrow} headline={page.cta.headline} sub={page.cta.sub} />
    </div>
  );
}
